/**
 * Shared fixture utilities for both editor and frontend
 */

/**
 * Safely create a Date object with validation
 *
 * @param {string|Date} dateInput - Date string or Date object
 * @return {Date|null} Valid Date object or null if invalid
 */
export function safeCreateDate(dateInput) {
  if (!dateInput) {
    return null;
  }

  // If already a Date object, return it
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }

  // Handle YYYY-MM-DD format explicitly to avoid timezone issues
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [year, month, day] = dateInput.split('-').map(Number);
    const date = new Date(year, month - 1, day); // Local timezone
    return isNaN(date.getTime()) ? null : date;
  }

  // Fallback to default Date constructor
  const date = new Date(dateInput);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Date utility functions
 */
export const dateUtils = {
  /**
   * Convert date to ISO format (YYYY-MM-DD)
   *
   * @param {Date} date - Date object (defaults to current date)
   * @return {string} Date in YYYY-MM-DD format
   */
  toISODate: (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  /**
   * Add days to a date
   *
   * @param {string|Date} date - Starting date
   * @param {number}      days - Number of days to add (can be negative)
   * @return {Date|null} New date object or null if invalid
   */
  addDays: (date, days) => {
    const newDate = new Date(date);
    if (isNaN(newDate.getTime())) {
      return null;
    }
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  },
};

/**
 * Convert UTC datetime to user's local time
 *
 * @param {string} utcDatetime - UTC datetime string (e.g., "2025-07-30T16:00:00Z")
 * @return {string|null} Formatted local time or null if invalid
 */
function convertUtcToLocalTime(utcDatetime) {
  if (!utcDatetime) {
    return null;
  }

  try {
    const utcDate = new Date(utcDatetime);

    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      return null;
    }

    // Use Intl.DateTimeFormat to get user's local time
    return utcDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    return null;
  }
}

/**
 * Create DOM element for finished fixture score
 *
 * @param {Object} fixture - Fixture data
 * @return {HTMLElement} Score element
 */
function createScoreElement(fixture) {
  const scoreDiv = document.createElement('div');
  scoreDiv.className = 'wp-block-big-bite-stats-perform-fixtures__score';
  scoreDiv.textContent = `${fixture.home_score} - ${fixture.away_score}`;
  return scoreDiv;
}

/**
 * Create DOM element for live fixture status
 *
 * @param {string} liveText - Translated 'LIVE' text
 * @return {HTMLElement} Live status element
 */
function createLiveStatusElement(liveText = 'LIVE') {
  const statusDiv = document.createElement('div');
  statusDiv.className =
    'wp-block-big-bite-stats-perform-fixtures__status wp-block-big-bite-stats-perform-fixtures__status--live';
  statusDiv.textContent = liveText;
  return statusDiv;
}

/**
 * Create DOM element for fixture time
 *
 * @param {string} displayTime - Time to display
 * @return {HTMLElement} Time element
 */
function createTimeElement(displayTime) {
  const timeDiv = document.createElement('div');
  timeDiv.className = 'wp-block-big-bite-stats-perform-fixtures__time';
  timeDiv.textContent = displayTime;
  return timeDiv;
}

/**
 * Get display time for a fixture
 *
 * @param {Object} fixture - Fixture data
 * @return {string} Display time (backend provides TBD for unknown times)
 */
function getFixtureDisplayTime(fixture) {
  const localTime = fixture.utc_datetime ? convertUtcToLocalTime(fixture.utc_datetime) : null;
  return localTime || fixture.time;
}

/**
 * Render fixture time/score based on status
 *
 * @param {Object}  fixture  - Fixture data
 * @param {boolean} isReact  - Whether to return React JSX or DOM element
 * @param {string}  liveText - Translated 'LIVE' text (optional)
 * @return {JSX.Element|HTMLElement|string} Rendered time/score element
 */
export function renderFixtureTime(fixture, isReact = true, liveText = 'LIVE') {
  const hasScore = fixture.home_score !== null && fixture.away_score !== null;
  const isFinished = fixture.status === 'finished' && hasScore;
  const isLive = fixture.status === 'live' && hasScore;

  if (isFinished || isLive) {
    const scoreText = `${fixture.home_score} - ${fixture.away_score}`;
    return isReact ? scoreText : createScoreElement(fixture);
  }

  if (fixture.status === 'live') {
    return isReact ? liveText : createLiveStatusElement(liveText);
  }

  const displayTime = getFixtureDisplayTime(fixture);
  return isReact ? displayTime : createTimeElement(displayTime);
}

/**
 * Generate array of dates around a center date
 *
 * @param {string} centerDate - Center date in YYYY-MM-DD format
 * @param {number} daysAround - Number of days to show on each side
 * @return {Array} Array of date objects
 */
export function generateDateRange(centerDate, daysAround = 6) {
  const dates = [];
  const center = safeCreateDate(centerDate);

  if (!center) {
    return dates;
  }

  for (let i = -daysAround; i <= daysAround; i++) {
    const date = new Date(center);
    date.setDate(center.getDate() + i);

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    dates.push({
      date: dateUtils.toISODate(date), // YYYY-MM-DD format in local timezone
      dayName: date.toLocaleDateString(undefined, { weekday: 'short' }),
      dayNumber: date.getDate(),
      isToday,
    });
  }

  return dates;
}

/**
 * Group fixtures by competition (sorting is handled server-side)
 *
 * @param {Array} fixtures - Array of fixture objects
 * @return {Array} Array of competition groups with fixtures
 */
export function groupFixturesByCompetition(fixtures) {
  const competitionGroups = new Map();

  fixtures.forEach((fixture) => {
    const competition = fixture.competition || 'Other';

    if (!competitionGroups.has(competition)) {
      competitionGroups.set(competition, []);
    }

    competitionGroups.get(competition).push(fixture);
  });

  // Convert Map to array and sort by priority, then alphabetically
  return Array.from(competitionGroups.entries()).map(([competition, fixtureList]) => ({
    competition,
    fixtures: fixtureList,
  }));
}

/**
 * Create fixture item HTML structure for DOM rendering
 *
 * @param {Object} fixture  - Fixture data
 * @param {string} liveText - Translated 'LIVE' text (optional)
 * @param {string} basePath - Base path for dynamic match pages
 * @return {HTMLElement}    - Fixture item element
 */
export function createFixtureElement(fixture, liveText = 'LIVE', basePath) {
  const fixtureDiv = document.createElement('div');
  fixtureDiv.className = 'wp-block-big-bite-stats-perform-fixtures__item';
  fixtureDiv.setAttribute(
    'data-competition',
    fixture.competition ? fixture.competition.toLowerCase().replace(/\s+/g, '-') : '',
  );

  // Add live class for live matches
  if (fixture.status === 'live') {
    fixtureDiv.classList.add('wp-block-big-bite-stats-perform-fixtures__item--live');
  }

  // Add clickable class and link to dynamic match page if fixture has an ID
  if (fixture.id) {
    fixtureDiv.classList.add('wp-block-big-bite-stats-perform-fixtures__item--clickable');
    const dynamicMatchUrl = `/${basePath}/${fixture.id}`;
    fixtureDiv.setAttribute('data-match-url', dynamicMatchUrl);
    fixtureDiv.setAttribute('data-match-id', fixture.id);
    fixtureDiv.style.cursor = 'pointer';

    // Generate title based on team names
    const matchTitle = `${fixture.home_team || fixture.home_team_short || 'Home'} v ${fixture.away_team || fixture.away_team_short || 'Away'}`;
    fixtureDiv.setAttribute('title', `View match page: ${matchTitle}`);

    // Add click handler
    fixtureDiv.addEventListener('click', () => {
      window.location.href = dynamicMatchUrl;
    });
  }

  // Teams container
  const teamsDiv = document.createElement('div');
  teamsDiv.className = 'wp-block-big-bite-stats-perform-fixtures__teams';

  // Home team
  const homeTeamDiv = document.createElement('div');
  homeTeamDiv.className = 'wp-block-big-bite-stats-perform-fixtures__team--home';
  homeTeamDiv.innerHTML = `
    <span class="wp-block-big-bite-stats-perform-fixtures__team-name">${fixture.home_team_short}</span>
  `;

  // Time/score
  const timeElement = renderFixtureTime(fixture, false, liveText);

  // Away team
  const awayTeamDiv = document.createElement('div');
  awayTeamDiv.className = 'wp-block-big-bite-stats-perform-fixtures__team--away';
  awayTeamDiv.innerHTML = `
    <span class="wp-block-big-bite-stats-perform-fixtures__team-name">${fixture.away_team_short}</span>
  `;

  teamsDiv.appendChild(homeTeamDiv);
  teamsDiv.appendChild(timeElement);
  teamsDiv.appendChild(awayTeamDiv);
  fixtureDiv.appendChild(teamsDiv);

  // Meta information (venue only - competition shown in group header)
  if (fixture.venue) {
    const metaDiv = document.createElement('div');
    metaDiv.className = 'wp-block-big-bite-stats-perform-fixtures__meta';

    const venueSpan = document.createElement('span');
    venueSpan.className = 'wp-block-big-bite-stats-perform-fixtures__venue';
    venueSpan.textContent = fixture.venue;
    metaDiv.appendChild(venueSpan);

    fixtureDiv.appendChild(metaDiv);
  }

  return fixtureDiv;
}
